import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import Confetti from 'react-confetti';
import { LuSparkles } from 'react-icons/lu';
import { elliottTokens as t } from '../tokens';
import { MOBILE_SX } from '../../../../../../mobile/mobileApp';

// Lesson-complete celebration — dimmed backdrop + confetti + card (matches the production pattern).
const CelebrationModal: React.FC<{
  onContinue: () => void;
  /** When there is a next unlocked lesson, show a primary CTA straight into it. */
  onNextLesson?: () => void;
  nextLessonTitle?: string;
  /** Copy overrides — used by the Improve Performance all-clear celebration. */
  title?: string;
  subtitle?: string;
}> = ({ onContinue, onNextLesson, nextLessonTitle, title, subtitle }) => {
  const [size] = useState({ w: window.innerWidth, h: window.innerHeight });

  return (
    <Box
      onClick={onContinue}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        bgcolor: 'rgba(0,0,0,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2
      }}
    >
      <Confetti
        width={size.w}
        height={size.h}
        recycle={false}
        numberOfPieces={600}
        gravity={1}
        initialVelocityY={20}
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
      />
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: 520,
          maxWidth: '100%',
          bgcolor: t.bg,
          border: `1px solid ${t.borderContrast}`,
          borderRadius: '12px',
          p: '32px',
          [MOBILE_SX]: { p: '24px 20px' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          textAlign: 'center'
        }}
      >
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: t.textPrimary,
            background: t.brandyTextGradient
          }}
        >
          <LuSparkles size={28} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: t.textPrimary, mb: '6px' }}>
            {title || 'Great Job!'}
          </Typography>
          <Typography sx={{ fontSize: 16, color: t.textSecondary }}>
            {subtitle || 'You completed the lesson. Keep it up!'}
          </Typography>
        </Box>
        <Box sx={{ mt: '4px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          {onNextLesson && (
            <Box
              onClick={onNextLesson}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: '28px',
                py: '10px',
                borderRadius: '8px',
                cursor: 'pointer',
                color: t.textPrimary,
                fontWeight: 600,
                fontSize: 15,
                maxWidth: 420,
                background: t.brandyTextGradient,
                transition: 'filter 0.15s ease',
                '&:hover': { filter: 'brightness(1.08)' }
              }}
            >
              {nextLessonTitle ? `Next Lesson: ${nextLessonTitle}` : 'Next Lesson'}
            </Box>
          )}
          <Box
            onClick={onContinue}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: '28px',
              py: '10px',
              borderRadius: '8px',
              cursor: 'pointer',
              color: t.textPrimary,
              fontWeight: 600,
              fontSize: 15,
              // Next Lesson (when present) takes the brandy primary; Continue drops to an outline.
              background: onNextLesson ? 'transparent' : t.brandyTextGradient,
              border: onNextLesson ? `1px solid ${t.borderContrast}` : 'none',
              transition: 'filter 0.15s ease',
              '&:hover': { filter: 'brightness(1.08)' }
            }}
          >
            Continue
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CelebrationModal;
